import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { CampaignFilterType } from '../pages/MarketingPage/Components/CampaignTypesFilter/CampaignFilterTypes'
import { CAMPAIGN_FILTER_DEFAULT } from '../pages/MarketingPage/constants'
import { getCampaignsByBrandId_api } from 'api/campaignApi'
import { CampaignDetails } from 'modules/Types'
import { deepCopy } from 'modules/Utils'
import useBrand from './useBrand'
import { BrandExistsState } from './useBrand'
import { PLAYBOOK_TYPE_INFO } from 'modules/Const'

interface MarketingCampaignsState {
    // State
    campaigns: CampaignDetails[]
    campaignDrafts: CampaignDetails[]
    isLoadingCampaigns: boolean
    isLoadingMore: boolean
    campaignParams: CampaignFilterType // NOTE: OLD
    campaignFilter: CampaignFilterType
    campaignDraftFilter: CampaignFilterType
    isCampaignsLimitReached: boolean
    isCampaignDraftsLimitReached: boolean
    isCampaignFilter: boolean
    isCampaignsInitialized: boolean

    // Actions
    setCampaigns: (campaigns: CampaignDetails[]) => void
    setCampaignDrafts: (campaigns: CampaignDetails[]) => void
    setCampaignParams: (params: CampaignFilterType) => void
    setCampaignFilter: (filter: CampaignFilterType) => void
    setCampaignDraftFilter: (filter: CampaignFilterType) => void
    appendCampaigns: (newCampaigns: CampaignDetails[]) => void
    setLoading: (loading: boolean) => void
    setLoadingMore: (loading: boolean) => void
    setIsCampaignsLimitReached: (reached: boolean) => void
    setIsCampaignDraftsLimitReached: (reached: boolean) => void
    resetCampaignFilter: (brandId: number) => Promise<void>
    setIsCampaignFilter: (isCampaignFilter: boolean) => void
    setIsCampaignsInitialized: (isInitialized: boolean) => void

    // Methods
    getCampaigns: ({ brandId, params, isAnalytics }: { brandId?: number, params?: CampaignFilterType, isAnalytics?: boolean }) => Promise<CampaignDetails[]>
    getCampaignDrafts: ({ brandId, params }: { brandId: number, params?: CampaignFilterType }) => Promise<CampaignDetails[]>
    refreshCampaigns: (brandId: number, params?: CampaignFilterType) => Promise<void>
    loadMoreCampaigns: (brandId: number) => Promise<void>
    getCampaignsInDraft: () => CampaignDetails[]
    resetMarketingCampaigns: () => void
}

export const useCampaign = create(immer<MarketingCampaignsState>((set, get) => ({
    campaigns: [],
    campaignDrafts: [],
    isLoadingCampaigns: false,
    isLoadingMore: false,
    firstRenderCount: 0,
    campaignParams: CAMPAIGN_FILTER_DEFAULT,
    campaignFilter: CAMPAIGN_FILTER_DEFAULT,
    campaignDraftFilter: CAMPAIGN_FILTER_DEFAULT,
    isCampaignsLimitReached: false,
    isCampaignDraftsLimitReached: false,
    isCampaignFilter: false,
    isCampaignsInitialized: false,

    setCampaigns: (campaigns) => set((state) => { state.campaigns = campaigns }),
    setCampaignDrafts: (campaigns) => set((state) => { state.campaignDrafts = campaigns }),
    setCampaignParams: (params) => set((state) => { state.campaignParams = params }),
    setCampaignFilter: (filter) => set((state) => { state.campaignFilter = filter }),
    setCampaignDraftFilter: (filter) => set((state) => { state.campaignDraftFilter = filter }),
    appendCampaigns: (newCampaigns) => set((state) => { state.campaigns = [...state.campaigns, ...newCampaigns] }),
    setLoading: (loading) => set((state) => { state.isLoadingCampaigns = loading }),
    setLoadingMore: (loading) => set((state) => { state.isLoadingMore = loading }),
    setIsCampaignsLimitReached: (reached) => set((state) => { state.isCampaignsLimitReached = reached }),
    setIsCampaignDraftsLimitReached: (reached) => set((state) => { state.isCampaignDraftsLimitReached = reached }),
    setIsCampaignFilter: (isCampaignFilter: boolean) => set((state) => { state.isCampaignFilter = isCampaignFilter }),
    setIsCampaignsInitialized: (isInitialized: boolean) => set((state) => { state.isCampaignsInitialized = isInitialized }),

    getCampaigns: async ({
        brandId,
        params = CAMPAIGN_FILTER_DEFAULT,
        isAnalytics = false
    }: {
        brandId?: number,
        params?: CampaignFilterType,
        isAnalytics?: boolean
    } = {}): Promise<CampaignDetails[]> => {
        const { currentBrand } = useBrand.getState() as BrandExistsState

        if (!brandId) {
            brandId = Number(currentBrand?.id)
        }

        set((state) => { state.isLoadingCampaigns = true })
        try {
            let campaignFilterLocal = deepCopy(params)
            campaignFilterLocal.campaignStatuses = campaignFilterLocal.campaignStatuses.filter(status => status !== 'DRAFT' && status !== 'ARCHIVED')

            if (isAnalytics) {
                campaignFilterLocal.isAnalytics = true
            }

            const campaigns = await getCampaignsByBrandId_api({
                brandId,
                params: campaignFilterLocal,
            })
            set((state) => {
                state.campaigns = campaigns || []
            })
            return campaigns
        } catch (error) {
            console.error('Error fetching campaigns:', error)
            return []
        } finally {
            set((state) => {
                state.isLoadingCampaigns = false
                state.isCampaignsInitialized = true
            })
        }
    },

    getCampaignDrafts: async ({
        brandId,
        params = CAMPAIGN_FILTER_DEFAULT
    }: {
        brandId: number,
        params?: CampaignFilterType
    }): Promise<CampaignDetails[]> => {
        set((state) => { state.isLoadingCampaigns = true })

        try {
            let campaignFilterLocalDrafts = deepCopy(params)
            campaignFilterLocalDrafts.campaignStatuses = ['DRAFT']
            const campaignDrafts = await getCampaignsByBrandId_api({
                brandId: Number(brandId),
                params: campaignFilterLocalDrafts,
            })
            set((state) => {
                state.campaignDrafts = campaignDrafts || []
            })
            return campaignDrafts
        } catch (error) {
            console.error('Error fetching campaigns:', error)
            return []
        } finally {
            set((state) => { state.isLoadingCampaigns = false })
        }
    },

    resetCampaignFilter: async (brandId: number) => {
        set((state) => {
            state.campaignFilter = CAMPAIGN_FILTER_DEFAULT
            state.isCampaignFilter = false
        })
        if (brandId) {
            await get().getCampaigns({
                brandId,
                params: CAMPAIGN_FILTER_DEFAULT
            })
        }
    },

    refreshCampaigns: async (brandId: number, params = CAMPAIGN_FILTER_DEFAULT) => {
        set((state) => {
            state.isLoadingCampaigns = true
            // state.hasMoreCampaigns = true
        })

        const campaignFilterLocal = { ...params, offset: 0 }
        const campaigns = await getCampaignsByBrandId_api({
            brandId: Number(brandId),
            params: campaignFilterLocal
        })

        set((state) => {
            state.campaigns = campaigns || []
            // state.hasMoreCampaigns = campaigns?.length === params.limit
            state.isLoadingCampaigns = false
            state.campaignParams = campaignFilterLocal
        })
    },

    loadMoreCampaigns: async (brandId) => {
        const state = get()
        // if (!state.hasMoreCampaigns) return

        const campaignFilterLocal = {
            ...state.campaignParams,
            offset: state.campaignParams.offset + state.campaignParams.limit
        }

        set((state) => { state.isLoadingMore = true })

        const newCampaigns = await getCampaignsByBrandId_api({
            brandId: Number(brandId),
            params: campaignFilterLocal
        })

        set((state) => {
            state.campaigns = [...state.campaigns, ...newCampaigns]
            state.campaignParams = campaignFilterLocal
            // state.hasMoreCampaigns = newCampaigns.length === state.campaignParams.limit
            state.isLoadingMore = false
        })
    },

    /** Gets up to 3 draft campaigns that have valid content and mapping data */
    getCampaignsInDraft: (): CampaignDetails[] => {
        const { campaigns } = get()
        if (!campaigns) {
            return []
        }

        return campaigns.filter((campaign: CampaignDetails) => {
            const { campaign_metadata, status } = campaign
            if (!campaign_metadata || status !== 'DRAFT') return false
            const { content, campaign_type } = campaign_metadata
            const mappingData = PLAYBOOK_TYPE_INFO[campaign_type]?.title
            return Boolean(content) && Boolean(mappingData)
        }).slice(0, 3)
    },

    resetMarketingCampaigns: () => {
        set((state) => {
            // state.campaigns = []
            // state.campaignDrafts = []
            state.isCampaignsLimitReached = false
            state.isCampaignDraftsLimitReached = false
        })
    }
}))
) 